import React, { useState, useEffect, useCallback } from "react";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../services/firebase";
import readyServicesTerms from "./terms/ready-services.json";
import orderServicesTerms from "./terms/order-services.json";
import openMarketTerms from "./terms/open-market.json";
import { useSeller } from "../contexts/SellerContext";
import PageHeader from "../components/PageHeader";
import "./SegmentRegistration.css";

const SegmentRegistration = () => {
  const [segment, setSegment] = useState("");
  const [existingRegistration, setExistingRegistration] = useState(null);
  const [isCheckingRegistration, setIsCheckingRegistration] = useState(true);
  const [agreeChecked, setAgreeChecked] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupContent, setPopupContent] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const nav = useNavigate();
  const { seller, clearSeller } = useSeller();

  const termsAndConditions = {
    "ready-services": readyServicesTerms,
    "order-services": orderServicesTerms,
    "open-market": openMarketTerms,
  };

  const handleAgreeChange = () => {
    setAgreeChecked(!agreeChecked);
  };

  // Check for existing segment registration
  useEffect(() => {
    const checkExistingRegistration = async () => {
      if (!seller || !seller.sellerId) {
        setIsCheckingRegistration(false);
        return;
      }
      try {
        const segmentDocRef = doc(
          db,
          "SellerSegmentRegistrations",
          seller.sellerId,
        );
        const segmentDoc = await getDoc(segmentDocRef);

        if (segmentDoc.exists()) {
          const registrationData = segmentDoc.data();
          setExistingRegistration(registrationData);
          setSegment(registrationData.segment);
        }
      } catch (error) {
        console.error("Error checking existing registration:", error);
      } finally {
        setIsCheckingRegistration(false);
      }
    };

    checkExistingRegistration();
  }, [seller]);

  const openPopup = (serviceType) => {
    setPopupContent(termsAndConditions[serviceType]);
    setShowPopup(true);
    document.body.style.overflow = "hidden";
  };

  const closePopup = () => {
    setShowPopup(false);
    setPopupContent(null);
    document.body.style.overflow = "auto";
  };

  const saveSegmentRegistration = useCallback(async () => {
    try {
      if (!seller || !seller.sellerId) {
        throw new Error("Seller not found in context");
      }

      const segmentDocRef = doc(
        db,
        "SellerSegmentRegistrations",
        seller.sellerId,
      );

      const registrationData = {
        sellerId: seller.sellerId,
        segment: segment,
        agreedToTerms: agreeChecked,
        registrationComplete: true,
        updatedAt: serverTimestamp(),
      };
      const sellerProfileRef = doc(db, "profile", seller.sellerId);
      await setDoc(
        sellerProfileRef,
        {
          SegmentRegistration: true,
          segment: segment,
        },
        { merge: true },
      );
      await setDoc(segmentDocRef, registrationData);

      await clearSeller();
      nav("/login", { replace: true });

      return true;
    } catch (error) {
      console.error("Error saving segment registration:", error);
      alert("Error registering segment. Please try again.");
      return false;
    }
  }, [segment, seller, agreeChecked,clearSeller,nav]);

  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (!segment) {
      alert("Please select a Segment");
      return;
    }

    if (!agreeChecked) {
      alert("Please agree to all terms before proceeding");
      return;
    }

    setIsSubmitting(true);
    try {
      const saved = await saveSegmentRegistration();
      if (!saved) return;
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isCheckingRegistration) {
    return (
      <>
        <PageHeader title="Segment Registration" />
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Checking registration status...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Segment Registration" />
      <div className="segment-registration-page">
        <div className="segment-registration-container">
          <div className="segment-registration-content-wrapper">
            <main className="main-content">
              {/* Segment Selection Section */}
              <section className="segment-information form-card">
                <div className="card-header">
                  <h2>Segment Information</h2>
                  <p className="card-subtitle">
                    Select your business segment and agree to terms
                  </p>
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="segment">Segment *</label>
                    {existingRegistration ? (
                      <input
                        type="text"
                        id="segment"
                        name="segment"
                        value={segment}
                        readOnly
                        className="read-only-segment"
                      />
                    ) : (
                      <select
                        id="segment"
                        name="segment"
                        value={segment}
                        onChange={(e) => {
                          setSegment(e.target.value);
                        }}
                        required
                      >
                        <option value="">Select Segment</option>
                        <option value="gold">Gold</option>
                        <option value="silver">Silver</option>
                        <option value="platinum">Platinum</option>
                        <option value="diamond">Diamond</option>
                        <option value="GEM">GEM</option>
                        <option value="PEARL">PEARL</option>
                        <option value="BANDHEL">BANDHEL</option>
                        <option value="DECOR">DECOR</option>
                        <option value="BRAND">BRAND</option>
                        <option value="TOOL">TOOL</option>
                        <option value="MACHINE">MACHINE</option>
                      </select>
                    )}
                  </div>
                </div>
              </section>

              {/* Terms Agreement Section */}
              <section className="service-selection form-card">
                <div className="card-header">
                  <h2>Terms & Agreement</h2>
                  <p className="card-subtitle">
                    Agree to all available services with Terms & Conditions
                  </p>
                </div>

                <div className="services-grid">
                  <div className="checkbox-card">
                    <div className="checkbox-left">
                      <input
                        type="checkbox"
                        id="ready-services"
                        name="services"
                        checked={agreeChecked}
                        onChange={handleAgreeChange}
                      />
                      <label htmlFor="ready-services">Ready Services</label>
                    </div>
                    <button
                      type="button"
                      className="terms-link"
                      onClick={() => openPopup("ready-services")}
                    >
                      Terms & Conditions
                    </button>
                  </div>

                  <div className="checkbox-card">
                    <div className="checkbox-left">
                      <input
                        type="checkbox"
                        id="order-services"
                        name="services"
                        checked={agreeChecked}
                        onChange={handleAgreeChange}
                      />
                      <label htmlFor="order-services">Order Services</label>
                    </div>
                    <button
                      type="button"
                      className="terms-link"
                      onClick={() => openPopup("order-services")}
                    >
                      Terms & Conditions
                    </button>
                  </div>

                  <div className="checkbox-card">
                    <div className="checkbox-left">
                      <input
                        type="checkbox"
                        id="open-market"
                        name="services"
                        checked={agreeChecked}
                        onChange={handleAgreeChange}
                      />
                      <label htmlFor="open-market">Open Market</label>
                    </div>
                    <button
                      type="button"
                      className="terms-link"
                      onClick={() => openPopup("open-market")}
                    >
                      Terms & Conditions
                    </button>
                  </div>
                </div>

                <div className="checkbox-card agreement-card">
                  <input
                    type="checkbox"
                    id="agree"
                    checked={agreeChecked}
                    onChange={handleAgreeChange}
                    required
                  />
                  <label htmlFor="agree">
                    I agree to all available services with Terms & Conditions *
                  </label>
                </div>
              </section>
            </main>

            <footer className="sticky-footer">
              <div className="footer-inner">
                <button
                  type="button"
                  className="draft-btn"
                  onClick={() => nav(-1)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="submit-btn"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Saving..."
                    : "Register Segment"}
                </button>
              </div>
            </footer>
          </div>

          {/* Terms & Conditions Popup */}
          {showPopup && popupContent && (
            <div className="popup-overlay" onClick={closePopup}>
              <div
                className="popup-content"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="popup-header">
                  <h3>{popupContent.title}</h3>
                  <button
                    className="close-btn"
                    onClick={closePopup}
                    aria-label="Close modal"
                  >
                    ×
                  </button>
                </div>
                <div className="popup-body">
                  {popupContent.content.map((section, index) => (
                    <div key={index} className="terms-section">
                      <h4>{section.section}</h4>
                      <p>{section.text}</p>
                    </div>
                  ))}
                </div>
                <div className="popup-footer">
                  <button
                    type="button"
                    className="popup-close-btn"
                    onClick={closePopup}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SegmentRegistration;
